import { Outlet } from "react-router-dom";
import { TabBar } from "@fox1ck-org/ui";

import { useT, type TKey } from "@/lib/i18n";
import { useRouteTab } from "@/lib/route-tab";

/**
 * The tab strip of the admin area, whose screens are tabs (Настройки draws
 * its own: pages/settings/section). Each tab is an address (lib/route-tab), so a tab survives a
 * reload and can be sent as a link; each screen still renders its own `Page`
 * with its own header, so nothing here repeats a title.
 */
function SectionTabs<T extends string>({
  base,
  tabs,
  index,
  labels,
  label,
}: {
  base: string;
  tabs: readonly T[];
  index: T;
  labels: Record<T, TKey>;
  label: string;
}) {
  const t = useT();
  const { tab, setTab } = useRouteTab(base, tabs, index);
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 px-5 pt-2">
        <TabBar
          aria-label={label}
          value={tab}
          onValueChange={(v) => setTab(v as T)}
          tabs={tabs.map((value) => ({ value, label: t(labels[value]) }))}
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </div>
    </div>
  );
}

const ADMIN_TABS = ["flows", "flow-templates", "tag-manager", "detectors", "fraud-tuning", "ad-link-templates", "ip-intel", "edge"] as const;
const ADMIN_LABEL: Record<(typeof ADMIN_TABS)[number], TKey> = {
  flows: "admin.tab.flows",
  "flow-templates": "admin.tab.flowTemplates",
  "tag-manager": "admin.tab.tagManager",
  detectors: "admin.tab.detectors",
  "fraud-tuning": "admin.tab.fraudWeights",
  "ad-link-templates": "admin.tab.adLinks",
  "ip-intel": "admin.tab.ipIntel",
  edge: "admin.tab.edge",
};

/**
 * The admin area. Every screen in it changes something outside the buyer's
 * own workspace — routing templates, tag snippets, the weights the fraud
 * engine scores every tenant's traffic with — so the section is gated as a
 * whole rather than screen by screen. The gate is repeated on each route in
 * `App.tsx`: a shell is a layout, and a layout is not a permission.
 *
 * `/admin` itself redirects to «Потоки», so that tab's address is the bare one.
 */
export function AdminSection() {
  const t = useT();
  return <SectionTabs base="/admin" tabs={ADMIN_TABS} index="flows" labels={ADMIN_LABEL} label={t("nav.admin")} />;
}
